import { LooseAutocomplete, PriceValue } from '@b2b/types/core';
import { DiscountType, TicketConversionStatus } from '@b2b/types/order/api/enums';
import { CouponVo } from '@b2b/types/order/api/will-call-order-detail.type';

export interface DiscountMeta {
  discountId?: string;
  discountName?: string;
  discountType?: DiscountType;
  discountValue?: PriceValue;
  couponDetail?: CouponVo;
}

/**
 * Picks discount and coupon fields from a source object.
 * Centralizes the repeated pattern of spreading discount metadata onto new order items.
 */
export function pickDiscountMeta(source: DiscountMeta | null | undefined): DiscountMeta {
  return {
    discountId: source?.discountId,
    discountName: source?.discountName,
    discountType: source?.discountType,
    discountValue: source?.discountValue,
    couponDetail: source?.couponDetail,
  };
}

/**
 * Normalizes a quantity value to a valid integer.
 * Converts strings and numbers to integers, returns 0 for invalid values.
 *
 * @param quantity - The quantity value to normalize (can be string or number)
 * @returns A valid integer (0 if input is invalid)
 *
 * @example
 * normalizeQuantity('5') // returns 5
 * normalizeQuantity(10) // returns 10
 * normalizeQuantity('abc') // returns 0
 * normalizeQuantity('') // returns 0
 */
export function normalizeQuantity(quantity: string | number): number {
  const value = parseInt(quantity as string, 10);
  return isNaN(value) ? 0 : value;
}

/**
 * Checks if an event item has a discount applied.
 * Compares the discounted unit price with the regular unit price.
 *
 * @param eventItem - The event item to check
 * @returns true if the item is discounted, false otherwise
 *
 * @example
 * isDiscountedItem({ discountedUnitPrice: 10, unitPrice: 15 }) // returns true
 * isDiscountedItem({ discountedUnitPrice: 10, unitPrice: 10 }) // returns false
 */
export function isDiscountedItem(eventItem: {
  discountedUnitPrice: PriceValue;
  unitPrice: PriceValue;
}): boolean {
  return eventItem.discountedUnitPrice !== eventItem.unitPrice;
}

/**
 * Checks if an item is a membership conversion.
 *
 * @param item - The item to check
 * @returns true if the item is a membership conversion, false otherwise
 *
 * @example
 * isMembershipConversion({ conversionStatus: 'MembershipConversion' }) // returns true
 * isMembershipConversion({ conversionStatus: 'Regular' }) // returns false
 * isMembershipConversion(null) // returns false
 */
export function isMembershipConversion(item: {
  conversionStatus: TicketConversionStatus;
}): boolean {
  return Boolean(item && item.conversionStatus && item.conversionStatus === 'MembershipConversion');
}

/**
 * Checks if an event is editable.
 * Membership events are not editable.
 *
 * @param event - The event to check
 * @returns true if the event is editable, false otherwise
 *
 * @example
 * isEventEditable({ eventId: 'membership' }) // returns false
 * isEventEditable({ eventId: 'MEMBERSHIP' }) // returns false
 * isEventEditable({ eventId: 'concert-123' }) // returns true
 */
export function isEventEditable(event: {
  eventId: LooseAutocomplete<'membership'>;
}): boolean {
  if (event.eventId.toLowerCase() === 'membership') {
    return false;
  }
  return true;
}

/**
 * Checks if an event is an addon.
 *
 * @param event - The event to check
 * @returns true if the event is an addon, false otherwise
 *
 * @example
 * isAddon({ eventId: 'addons' }) // returns true
 * isAddon({ eventId: 'ADDONS' }) // returns true
 * isAddon({ eventId: 'concert-123' }) // returns false
 */
export function isAddon(event: {
  eventId: LooseAutocomplete<'addons'>;
}): boolean {
  return event.eventId.toLowerCase() === 'addons';
}

/**
 * Type guard to check if an item type is an Event or Combo.
 *
 * @param type - The item type to check
 * @returns true if the type is 'Event' or 'Combo', false otherwise
 *
 * @example
 * isEventItemType('Event') // returns true
 * isEventItemType('Combo') // returns true
 * isEventItemType('Inventory') // returns false
 */
export function isEventItemType(
  type: LooseAutocomplete<'Event' | 'Combo'>
): boolean {
  return type === 'Event' || type === 'Combo';
}

/**
 * Type guard to check if an item type is Inventory or ComboInventory.
 *
 * @param type - The item type to check
 * @returns true if the type is 'Inventory' or 'ComboInventory', false otherwise
 *
 * @example
 * isInventoryItemType('Inventory') // returns true
 * isInventoryItemType('ComboInventory') // returns true
 * isInventoryItemType('Event') // returns false
 */
export function isInventoryItemType(
  type: LooseAutocomplete<'Inventory' | 'ComboInventory'>
): boolean {
  return type === 'Inventory' || type === 'ComboInventory';
}

/**
 * Validates that every event has at least one item with a quantity greater than 0.
 *
 * Checks if EVERY event has AT LEAST ONE item with quantity > 0.
 * This allows events to have some items with zero quantity, as long as at least one item has a valid quantity.
 *
 * When `originalEventItems` is provided, only validates events that are NOT in the original order.
 * This allows users to remove items from existing events while requiring newly added events to have at least one item.
 *
 * @param eventItems - Array of events, each containing an array of items with quantities
 * @param originalEventItems - Optional array of original event items to exclude from validation
 * @returns True if every event (excluding original events) has at least one item with quantity > 0, false otherwise
 *
 * @example
 * ```typescript
 * // Valid - event has at least one item with quantity > 0
 * hasAllEventItemsWithQuantity([
 *   { items: [{ quantity: 1 }, { quantity: 2 }] }
 * ]); // true
 *
 * // Valid - event has at least one valid item (mixed quantities allowed)
 * hasAllEventItemsWithQuantity([
 *   { items: [{ quantity: 1 }, { quantity: 0 }] }
 * ]); // true
 *
 * // Invalid - event has NO items with quantity > 0
 * hasAllEventItemsWithQuantity([
 *   { items: [{ quantity: 0 }, { quantity: null }] }
 * ]); // false
 *
 * // Empty array (vacuous truth)
 * hasAllEventItemsWithQuantity([]); // true
 *
 * // Only validate newly added events (not in original order)
 * hasAllEventItemsWithQuantity(
 *   [{ eventId: '1', items: [{ quantity: 0 }] }, { eventId: '2', items: [{ quantity: 1 }] }],
 *   [{ eventId: '1', items: [] }]
 * ); // true (event '1' is in original order, so only event '2' is validated)
 * ```
 */
export function hasAllEventItemsWithQuantity(
  eventItems: Array<{ eventId?: string; items: Array<{ quantity?: number | null }> }>,
  originalEventItems?: Array<{ eventId?: string }>
): boolean {
  // If originalEventItems is provided, filter out events that were in the original order
  const eventsToValidate = originalEventItems
    ? eventItems.filter((event) => !originalEventItems.some((original) => original.eventId === event.eventId))
    : eventItems;

  return eventsToValidate.every((event) => event.items.some((item) => (item.quantity ?? 0) > 0));
}

/**
 * Determines if the "+ Discounted Price" link should be shown for an order item.
 * The link is shown when:
 * - The order is a member order (membershipId is present)
 * - The item is NOT already discounted (discountedUnitPrice >= unitPrice)
 * - A discount is available for this ticket type (findTicketDiscounted returns non-null)
 *
 * @param item - The order item to check
 * @param membershipId - The membership ID of the order (null/undefined if not a member order)
 * @param eventId - The event ID the item belongs to
 * @param findTicketDiscounted - Function that resolves a discount for an event + ticket type
 * @returns true if the "+ Discounted Price" link should be shown, false otherwise
 *
 * @example
 * ```typescript
 * shouldShowDiscountedPriceLink(
 *   { discountedUnitPrice: 10, unitPrice: 10, ticketTypeId: 'adult' },
 *   'membership-123',
 *   'event-456',
 *   (eventId, ticketTypeId) => ({ discountedPrice: '8.00' }),
 * ); // true
 *
 * shouldShowDiscountedPriceLink(
 *   { discountedUnitPrice: 8, unitPrice: 10, ticketTypeId: 'adult' },
 *   'membership-123',
 *   'event-456',
 *   (eventId, ticketTypeId) => ({ discountedPrice: '8.00' }),
 * ); // false (already discounted)
 * ```
 */
export interface ShouldShowDiscountedPriceLinkOptions {
  membershipId: string | null | undefined;
  eventId: string | undefined;
  findTicketDiscounted: ((eventId: string, ticketTypeId: string) => { discountedPrice: string } | null) | undefined;
}

export function shouldShowDiscountedPriceLink(
  item: {
    discountedUnitPrice?: string | number;
    unitPrice?: string | number;
    ticketTypeId?: string;
  },
  options: ShouldShowDiscountedPriceLinkOptions,
): boolean {
  const { membershipId, eventId, findTicketDiscounted } = options;

  // For member orders only
  if (!membershipId) {
    return false;
  }

  // Item must NOT already be discounted
  const isAlreadyDiscounted =
    item.discountedUnitPrice !== undefined &&
    item.unitPrice !== undefined &&
    Number(item.discountedUnitPrice) < Number(item.unitPrice);

  if (isAlreadyDiscounted) {
    return false;
  }

  // A discount must be available for this ticket type
  if (!findTicketDiscounted || !eventId || !item.ticketTypeId) {
    return false;
  }

  return findTicketDiscounted(eventId, item.ticketTypeId) !== null;
}

/**
 * Determines if the "Add Full Price" link should be shown for an order item.
 * The link is shown when:
 * - The order is NOT an organization order
 * - The item has a discount (discountedUnitPrice < unitPrice)
 *
 * @param item - The order item to check
 * @param isOrganizationOrder - Boolean indicating if this is an organization order
 * @returns true if the "Add Full Price" link should be shown, false otherwise
 *
 * @example
 * ```typescript
 * shouldShowFullPriceLink(
 *   { discountedUnitPrice: 10, unitPrice: 15 },
 *   false
 * ); // true (has discount, not org order)
 *
 * shouldShowFullPriceLink(
 *   { discountedUnitPrice: 10, unitPrice: 10 },
 *   false
 * ); // false (no discount)
 *
 * shouldShowFullPriceLink(
 *   { discountedUnitPrice: 10, unitPrice: 15 },
 *   true
 * ); // false (org order)
 * ```
 */
export function shouldShowFullPriceLink(
  item: {
    discountedUnitPrice?: string | number;
    unitPrice?: string | number;
  },
  isOrganizationOrder?: boolean
): boolean {
  // Don't show for organization orders
  if (isOrganizationOrder) {
    return false;
  }

  // Only show if item has a discount (discountedUnitPrice < unitPrice)
  // Note: Prices can be string | number union type, so Number() conversion is needed
  const hasDiscount =
    item.discountedUnitPrice !== undefined &&
    item.unitPrice !== undefined &&
    Number(item.discountedUnitPrice) < Number(item.unitPrice);

  return hasDiscount;
}
